import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CircleNotch, CalendarBlank, Trophy, CurrencyDollar, Plus, Trash,
  PaperPlaneTilt, PencilSimple, CheckCircle, DotsThreeVertical,
  HandPalm, Prohibit, X, WarningCircle, Megaphone,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { PointsKeyPanel } from "@/components/PointsKey";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShareNetwork, WhatsappLogo, ChatText, Copy, CalendarPlus } from "@phosphor-icons/react";
import { shareLeaderboard, shareViaSms, copyLeaderboard } from "@/lib/share";
import { addGameToCalendar, addAllGamesToCalendar } from "@/lib/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const PAY_STYLE = {
  paid: "bg-[#34C759]/20 text-[#34C759]",
  unpaid: "bg-kinetic/20 text-kinetic",
  refunded: "bg-[#FF9500]/20 text-[#FF9500]",
};

const PLACEMENT_POINTS = { 1: 25, 2: 18, 3: 14, 4: 11, 5: 9, 6: 7, 7: 6, 8: 5, 9: 4, 10: 3, 11: 2, 12: 1 };

function fmtDate(iso) {
  if (!iso) return "TBC";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [tab, setTab] = useState("leaderboard");
  const [board, setBoard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [joining, setJoining] = useState(false);
  const [resultsGame, setResultsGame] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, e] = await Promise.all([
        api.get(`/tournaments/${id}/leaderboard`),
        api.get(`/tournaments/${id}/entries`),
      ]);
      setBoard(b.data);
      setEntries(e.data);
    } catch (err) {
      toast.error("Could not load tournament");
    }
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (!board) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-primary" /></div>;

  const t = board.tournament;
  const games = board.games;
  const myEntry = entries.find((e) => e.user_id === user?.id);
  const canJoin = ["entries_open", "live"].includes(t.status) && !myEntry;
  const isFull = t.max_entries > 0 && (t.spots_taken ?? entries.length) >= t.max_entries;

  const joinNow = async () => {
    setJoining(true);
    try {
      await api.post(`/tournaments/${id}/join`);
      toast.success(t.requires_approval ? "Entry request sent — awaiting admin approval." : "You're in! Entry recorded.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <button onClick={() => navigate("/")} className="text-sm text-muted-foreground hover:text-white" data-testid="back-link">← All tournaments</button>
        {isAdmin && <AdminBar tournament={t} onChange={load} />}
      </div>
      <div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">{t.name}</h1>
        {t.description && <p className="text-muted-foreground mt-1 max-w-2xl">{t.description}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
          {t.entry_fee > 0 && <span>${t.entry_fee} buy-in</span>}
          {t.max_entries > 0 && <span>{t.spots_taken ?? entries.length}/{t.max_entries} spots</span>}
          <span className="capitalize">{(t.status || "").replace("_", " ")}</span>
        </div>
        <div className="mt-2.5">
          {myEntry ? (
            <span data-testid="my-entry-status" className={`inline-block text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm ${myEntry.status === "pending" ? "bg-[#FF9500]/20 text-[#FF9500]" : "bg-[#34C759]/20 text-[#34C759]"}`}>
              {myEntry.status === "pending" ? "Awaiting approval" : myEntry.status === "disqualified" ? "Disqualified" : myEntry.status === "stood_down" ? "Stood down" : "✓ You're entered"}
            </span>
          ) : canJoin && !isFull ? (
            <button onClick={joinNow} disabled={joining} data-testid="detail-join-button" className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest text-sm px-5 py-2.5 rounded-sm transition-colors">
              {joining ? "Joining…" : t.requires_approval ? "Request Entry" : "Enter Tournament"}
            </button>
          ) : isFull ? (
            <span className="text-xs font-bold uppercase tracking-widest text-kinetic">Tournament full</span>
          ) : null}
        </div>
      </div>

      {/* Tabs + share */}
      <div className="flex items-end justify-between gap-2 mt-5 border-b border-border">
        <div className="flex gap-1 overflow-x-auto">
          {[["leaderboard", "Leaderboard"], ["schedule", "Schedule"], ["players", `Players (${entries.length})`]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-3 sm:px-4 py-3 font-heading font-bold uppercase tracking-wide text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "leaderboard" && (
          <div className="pb-2 shrink-0">
            <ShareButton tournament={t} rows={board.rows} />
          </div>
        )}
      </div>

      <div className="mt-5">
        {tab === "leaderboard" && (
          <>
            <PointsKeyPanel className="mb-3" />
            <LeaderboardTable rows={board.rows} games={games} isAdmin={isAdmin} onCellClick={(gi) => setResultsGame(gi)} />
            {isAdmin && <p className="text-xs text-muted-foreground mt-2">Tip: tap any G column cell to enter or edit that game's results.</p>}
          </>
        )}
        {tab === "schedule" && <Schedule tournament={t} games={games} isAdmin={isAdmin} entries={entries} onChange={load} />}
        {tab === "players" && <Players tournament={t} entries={entries} isAdmin={isAdmin} onChange={load} />}
      </div>
      {resultsGame !== null && (
        <ResultsDialog tournamentId={id} gameIndex={resultsGame} entries={entries} onClose={() => setResultsGame(null)} onSaved={load} />
      )}
    </div>
  );
}

/* ---------------- Share ---------------- */
function ShareButton({ tournament, rows }) {
  const doShare = async () => {
    const res = await shareLeaderboard(tournament, rows);
    if (res === "whatsapp") toast.success("Opening WhatsApp…");
  };
  const copy = async () => { await copyLeaderboard(tournament, rows); toast.success("Standings copied to clipboard"); };
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={doShare}
        data-testid="share-leaderboard"
        className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest text-sm px-4 py-2 rounded-sm transition-colors"
      >
        <ShareNetwork size={18} weight="bold" /> Share
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-2 border border-border rounded-sm text-muted-foreground hover:text-white" data-testid="share-more" aria-label="More share options">⋯</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-card border-border" align="end">
          <DropdownMenuItem onClick={() => shareLeaderboard(tournament, rows)} data-testid="share-whatsapp" className="gap-2 cursor-pointer"><WhatsappLogo size={18} weight="fill" className="text-[#25D366]" /> WhatsApp</DropdownMenuItem>
          <DropdownMenuItem onClick={() => shareViaSms(tournament, rows)} data-testid="share-sms" className="gap-2 cursor-pointer"><ChatText size={18} weight="fill" className="text-primary" /> Text / SMS</DropdownMenuItem>
          <DropdownMenuItem onClick={copy} data-testid="share-copy" className="gap-2 cursor-pointer"><Copy size={18} weight="bold" /> Copy standings</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/* ---------------- Leaderboard ---------------- */
function LeaderboardTable({ rows, games, isAdmin, onCellClick }) {
  if (rows.length === 0) {
    return <div className="border border-border bg-card rounded-sm p-10 text-center text-muted-foreground">No players yet. Standings appear once players enter.</div>;
  }
  return (
    <div className="border border-border rounded-sm bg-card overflow-x-auto" data-testid="leaderboard-table">
      <table className="w-full min-w-[640px] border-collapse">
        <thead>
          <tr className="text-left border-b border-border">
            <th className="py-3 px-3 text-xs font-bold uppercase tracking-widest text-muted-foreground w-10">#</th>
            <th className="py-3 px-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Player</th>
            <th className="py-3 px-3 text-xs font-bold uppercase tracking-widest text-primary text-center bg-primary/5">Pts</th>
            <th className="py-3 px-3 text-xs font-bold uppercase tracking-widest text-muted-foreground text-center">W</th>
            {games.map((g) => (
              <th key={g.index}
                onClick={isAdmin ? () => onCellClick(g.index) : undefined}
                className={`py-3 px-2 text-xs font-bold uppercase tracking-widest text-center min-w-[52px] ${isAdmin ? "text-primary cursor-pointer hover:bg-primary/10" : "text-muted-foreground"}`}
                title={isAdmin ? `Enter results for ${g.name}` : g.name}
                data-testid={isAdmin ? `enter-results-header-${g.index}` : undefined}>
                G{g.index + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`leaderboard-row-${r.id}`} className="border-b border-border/60 hover:bg-[#1E1E1E] transition-colors">
              <td className="py-3 px-3 font-mono font-bold text-muted-foreground">
                {r.rank === 1 ? <Trophy size={18} weight="fill" className="text-[#FFD60A]" /> : r.rank}
              </td>
              <td className="py-3 px-3">
                <div className="flex items-center gap-2.5">
                  <Avatar name={r.display_name} path={r.avatar_path} size={34} enlargeable />
                  <div className="min-w-0">
                    <div className="font-semibold text-[15px] whitespace-nowrap flex items-center gap-1.5">
                      {r.nickname || shortName(r.display_name)}
                      {r.status === "stood_down" && <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">(out)</span>}
                    </div>
                    {(r.yellow_cards > 0 || r.red_cards > 0) && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {r.yellow_cards > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-muted-foreground" title={`${r.yellow_cards} yellow card(s)`}>
                            <span className="inline-block w-2 h-3 rounded-[1px] bg-[#FFD60A]" />{r.yellow_cards}
                          </span>
                        )}
                        {r.red_cards > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-muted-foreground" title={`${r.red_cards} red card(s)`}>
                            <span className="inline-block w-2 h-3 rounded-[1px] bg-kinetic" />{r.red_cards}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="py-3 px-3 text-center bg-primary/5">
                <span className="font-heading font-black text-2xl text-primary tabular-nums">{r.total_points}</span>
              </td>
              <td className="py-3 px-3 text-center font-mono text-muted-foreground">{r.wins}</td>
              {r.history.map((h) => (
                <td key={h.game_index}
                  onClick={isAdmin ? () => onCellClick(h.game_index) : undefined}
                  className={`py-3 px-2 text-center ${isAdmin ? "cursor-pointer hover:bg-primary/10" : ""}`}
                  data-testid={isAdmin ? `cell-${r.id}-g${h.game_index}` : undefined}>
                  {h.placement ? (
                    <div>
                      <div className="font-mono font-bold text-[15px] leading-none">{ordinal(h.placement)}</div>
                      <div className="font-mono text-[11px] text-primary/80 leading-none mt-0.5">+{h.points}</div>
                    </div>
                  ) : h.points != null ? (
                    <div title={h.dnp ? "Did not place" : "No show"}>
                      <div className="font-mono font-bold text-[13px] leading-none text-muted-foreground">{h.dnp ? "DNP" : "NS"}</div>
                      <div className="font-mono text-[11px] text-primary/80 leading-none mt-0.5">+{h.points}</div>
                    </div>
                  ) : (
                    <span className="text-border">–</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
function shortName(full) {
  if (!full) return "";
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

/* ---------------- Schedule ---------------- */
function Schedule({ tournament, games, isAdmin, entries, onChange }) {
  const [editing, setEditing] = useState(null); // game index for datetime edit
  const [dt, setDt] = useState("");
  const [resultsFor, setResultsFor] = useState(null);

  const saveGames = async (newGames) => {
    await api.patch(`/tournaments/${tournament.id}`, { games: newGames });
    toast.success("Schedule updated");
    onChange();
  };

  const saveDate = async (idx) => {
    const newGames = games.map((g) => ({ ...g, scheduled_at: g.index === idx ? (dt ? new Date(dt).toISOString() : null) : g.scheduled_at }));
    await saveGames(newGames);
    setEditing(null);
  };

  const hasDates = games.some((g) => g.scheduled_at);

  return (
    <div className="space-y-3">
      {hasDates && (
        <div className="flex justify-end">
          <button
            onClick={() => { addAllGamesToCalendar(tournament, games) ? toast.success("Calendar file downloaded — open it to add all games") : toast.error("No dates set yet"); }}
            data-testid="add-all-calendar"
            className="flex items-center gap-1.5 border border-border bg-card hover:bg-[#1E1E1E] text-white font-heading font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm transition-colors"
          >
            <CalendarPlus size={16} weight="bold" /> Add all to calendar
          </button>
        </div>
      )}
      {games.map((g) => (
        <div key={g.index} className="border border-border bg-card rounded-sm p-4 flex items-center justify-between gap-3" data-testid={`schedule-game-${g.index}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center font-heading font-black text-lg">{g.index + 1}</div>
            <div>
              <div className="font-heading font-bold text-lg uppercase tracking-tight flex items-center gap-2">
                {g.name}
                {g.status === "completed" && <CheckCircle size={16} weight="fill" className="text-[#34C759]" />}
              </div>
              {editing === g.index ? (
                <div className="flex items-center gap-2 mt-1">
                  <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} className="bg-secondary border border-border rounded-sm px-2 py-1 text-sm" data-testid={`date-input-${g.index}`} />
                  <button onClick={() => saveDate(g.index)} className="text-primary text-sm font-bold" data-testid={`save-date-${g.index}`}>Save</button>
                  <button onClick={() => setEditing(null)} className="text-muted-foreground text-sm">Cancel</button>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground flex items-center gap-1.5"><CalendarBlank size={14} weight="bold" />{fmtDate(g.scheduled_at)}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {g.scheduled_at && editing !== g.index && (
              <button
                onClick={() => { addGameToCalendar(tournament, g); toast.success("Calendar file downloaded — open it to add the game"); }}
                className="p-2 text-muted-foreground hover:text-primary"
                title="Add to my calendar"
                data-testid={`add-calendar-${g.index}`}
              >
                <CalendarPlus size={18} weight="bold" />
              </button>
            )}
            {isAdmin && editing !== g.index && (
              <>
                <button onClick={() => { setEditing(g.index); setDt(toLocalInput(g.scheduled_at)); }} className="p-2 text-muted-foreground hover:text-white" title="Set date/time" data-testid={`edit-date-${g.index}`}><PencilSimple size={18} weight="bold" /></button>
                <button onClick={() => setResultsFor(g.index)} className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-sm" data-testid={`enter-results-${g.index}`}>Results</button>
              </>
            )}
          </div>
        </div>
      ))}
      {resultsFor !== null && (
        <ResultsDialog tournamentId={tournament.id} gameIndex={resultsFor} entries={entries} onClose={() => setResultsFor(null)} onSaved={onChange} />
      )}
    </div>
  );
}

/* ---------------- Results entry ---------------- */
function ResultsDialog({ tournamentId, gameIndex, entries, onClose, onSaved }) {
  const approvedEntries = entries.filter((e) => (e.status || "approved") === "approved");
  const [sel, setSel] = useState({}); // entry_id -> "NS" | "1".."N" | ""
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/tournaments/${tournamentId}/games/${gameIndex}/results`).then(({ data }) => {
      const map = {};
      (data.rows || []).forEach((r) => {
        map[r.entry_id] = r.placement ? String(r.placement) : (r.dnp ? "DNP" : "NS");
      });
      setSel(map);
      setLoading(false);
    });
  }, [tournamentId, gameIndex]);

  const fieldSize = approvedEntries.length;
  const maxPos = Math.min(12, fieldSize);
  const pointsFor = (pos) => PLACEMENT_POINTS[pos] ?? 0;

  // numeric positions already taken by other players (to prevent duplicates)
  const takenPositions = (excludeId) =>
    new Set(approvedEntries.filter((e) => e.id !== excludeId && sel[e.id] && !isNaN(Number(sel[e.id]))).map((e) => Number(sel[e.id])));

  // Display order: placed players by position, then DNP / No Show / suspended, then not-yet-entered.
  const orderKey = (e) => {
    const suspended = (e.suspended_games || []).includes(gameIndex);
    const v = sel[e.id];
    if (v && !isNaN(Number(v))) return Number(v);
    if (suspended || v === "NS" || v === "DNP") return 100000;
    return 200000;
  };
  const sortedEntries = [...approvedEntries].sort((a, b) => orderKey(a) - orderKey(b));

  const fillRemainingDNP = () => {
    setSel((prev) => {
      const next = { ...prev };
      approvedEntries.forEach((e) => {
        const suspended = (e.suspended_games || []).includes(gameIndex);
        if (!suspended && !next[e.id]) next[e.id] = "DNP";
      });
      return next;
    });
  };
  const unsetCount = approvedEntries.filter((e) => !(e.suspended_games || []).includes(gameIndex) && !sel[e.id]).length;

  const save = async () => {
    setSaving(true);
    try {
      const results = approvedEntries.map((e) => {
        const v = sel[e.id];
        if (v && !isNaN(Number(v))) return { entry_id: e.id, placement: Number(v) };
        if (v === "DNP") return { entry_id: e.id, status: "dnp" };
        return { entry_id: e.id, status: "ns" };
      });
      await api.put(`/tournaments/${tournamentId}/games/${gameIndex}/results`, { results });
      toast.success("Results saved & leaderboard updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Game {gameIndex + 1} — Placings</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Award placings 1st–12th (each once): 1st <b className="text-white">25</b>, 2nd <b className="text-white">18</b>, 3rd <b className="text-white">14</b>, 4th <b className="text-white">11</b>, 5th <b className="text-white">9</b>, 6th <b className="text-white">7</b>, 7th <b className="text-white">6</b>, 8th <b className="text-white">5</b>, 9th <b className="text-white">4</b>, 10th <b className="text-white">3</b>, 11th <b className="text-white">2</b>, 12th <b className="text-white">1</b>. Everyone else is <b className="text-white">DNP</b> (did not place) or <b className="text-white">NS</b> (no show) — both score 0.</p>
        {loading ? <div className="py-8 flex justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div> : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {approvedEntries.length === 0 && <p className="text-muted-foreground text-sm">No approved players to score yet.</p>}
            {approvedEntries.length > 0 && (
              <div className="flex justify-end pb-1">
                <button
                  onClick={fillRemainingDNP}
                  disabled={unsetCount === 0}
                  data-testid="fill-remaining-dnp"
                  className="text-xs font-bold uppercase tracking-widest text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
                >
                  Fill remaining as DNP{unsetCount > 0 ? ` (${unsetCount})` : ""}
                </button>
              </div>
            )}
            {sortedEntries.map((e) => {
              const suspended = (e.suspended_games || []).includes(gameIndex);
              const taken = takenPositions(e.id);
              const current = sel[e.id] || "";
              return (
                <div key={e.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar name={e.nickname || e.display_name} path={e.avatar_path} size={30} />
                    <span className="font-medium truncate">{e.nickname || e.display_name}</span>
                  </div>
                  {suspended ? (
                    <span className="text-xs font-bold uppercase tracking-widest text-[#FF9500] shrink-0">Suspended · NS</span>
                  ) : (
                    <Select value={current} onValueChange={(v) => setSel((p) => ({ ...p, [e.id]: v }))}>
                      <SelectTrigger className="w-36 h-9 bg-secondary border-border font-semibold" data-testid={`placement-select-${e.id}`}>
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-64">
                        {Array.from({ length: maxPos }, (_, i) => i + 1).map((pos) => (
                          <SelectItem key={pos} value={String(pos)} disabled={taken.has(pos) && Number(current) !== pos}>
                            {ordinal(pos)} · {pointsFor(pos)}pts
                          </SelectItem>
                        ))}
                        <SelectItem value="DNP">DNP · Did not place · 0pts</SelectItem>
                        <SelectItem value="NS">NS · No Show · 0pts</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-results" className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest w-full">{saving ? "Saving…" : "Save Results"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Players / Entries ---------------- */
const STATUS_STYLE = {
  pending: "bg-[#FF9500]/20 text-[#FF9500]",
  approved: "bg-[#34C759]/20 text-[#34C759]",
  stood_down: "bg-muted text-muted-foreground",
  disqualified: "bg-kinetic/20 text-kinetic",
};
const STATUS_LABEL = { pending: "Pending", approved: "Approved", stood_down: "Stood Down", disqualified: "DQ" };

function Players({ tournament, entries, isAdmin, onChange }) {
  const [guest, setGuest] = useState("");
  const [cardFor, setCardFor] = useState(null); // {entry, type}
  const fee = tournament.entry_fee || 0;
  const paidCount = entries.filter((e) => e.payment_status === "paid").length;
  const activeEntries = entries.filter((e) => (e.status || "approved") !== "disqualified");
  const totalDue = activeEntries.length * fee;
  const collected = paidCount * fee;
  const pendingCount = entries.filter((e) => e.status === "pending").length;

  const addGuest = async () => {
    if (!guest.trim()) return;
    await api.post(`/tournaments/${tournament.id}/entries`, { name: guest.trim() });
    setGuest("");
    toast.success("Player added");
    onChange();
  };
  const setPayment = async (id, status) => { await api.patch(`/entries/${id}`, { payment_status: status }); onChange(); };
  const setStatus = async (id, status, msg) => {
    await api.patch(`/entries/${id}`, { status });
    if (msg) toast.success(msg);
    onChange();
  };
  const remove = async (id) => { await api.delete(`/entries/${id}`); toast.success("Removed"); onChange(); };
  const clearCards = async (id) => { await api.post(`/entries/${id}/clear-cards`); toast.success("Cards cleared"); onChange(); };

  return (
    <div>
      {isAdmin && fee > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat label="Collected" value={`$${collected}`} accent="#34C759" />
          <Stat label="Outstanding" value={`$${Math.max(totalDue - collected, 0)}`} accent="#FF3B30" />
          <Stat label="Paid" value={`${paidCount}/${activeEntries.length}`} accent="#007AFF" />
        </div>
      )}
      {isAdmin && (tournament.max_entries > 0 || tournament.payment_deadline || tournament.requires_approval) && (
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          {tournament.max_entries > 0 && <span className="border border-border bg-card rounded-sm px-2.5 py-1.5"><b>{tournament.spots_taken ?? activeEntries.length}/{tournament.max_entries}</b> spots</span>}
          {tournament.requires_approval && pendingCount > 0 && <span className="bg-[#FF9500]/20 text-[#FF9500] rounded-sm px-2.5 py-1.5 font-bold">{pendingCount} awaiting approval</span>}
          {tournament.payment_deadline && <span className="border border-border bg-card rounded-sm px-2.5 py-1.5">Pay by {fmtDate(tournament.payment_deadline)}</span>}
        </div>
      )}
      {isAdmin && (
        <div className="flex gap-2 mb-4">
          <Input value={guest} onChange={(e) => setGuest(e.target.value)} placeholder="Add player by name…" className="bg-card border-border" data-testid="guest-name-input" onKeyDown={(e) => e.key === "Enter" && addGuest()} />
          <Button onClick={addGuest} data-testid="add-guest-button" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase"><Plus size={18} weight="bold" /></Button>
        </div>
      )}
      {entries.length === 0 && <div className="border border-border bg-card rounded-sm p-10 text-center text-muted-foreground">No players entered yet.</div>}
      <div className="space-y-2">
        {entries.map((e) => {
          const status = e.status || "approved";
          return (
          <div key={e.id} className="border border-border bg-card rounded-sm p-3 flex items-center justify-between gap-3" data-testid={`entry-row-${e.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={e.display_name} path={e.avatar_path} size={38} enlargeable />
              <div className="min-w-0">
                <div className="font-semibold truncate flex items-center gap-1.5 flex-wrap">
                  {e.nickname || e.display_name}
                  {e.nickname && <span className="text-xs text-muted-foreground font-normal">({e.display_name})</span>}
                  {!e.user_id && <span className="text-xs text-muted-foreground">(guest)</span>}
                  {e.yellow_cards > 0 && <span title="Yellow cards" className="inline-flex items-center gap-0.5 text-[10px] font-bold"><span className="inline-block w-2.5 h-3.5 rounded-[1px] bg-[#FFD60A]" />{e.yellow_cards}</span>}
                  {e.red_cards > 0 && <span title="Red cards" className="inline-flex items-center gap-0.5 text-[10px] font-bold"><span className="inline-block w-2.5 h-3.5 rounded-[1px] bg-kinetic" />{e.red_cards}</span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${STATUS_STYLE[status]}`} data-testid={`entry-status-${e.id}`}>{STATUS_LABEL[status]}</span>
                  {e.suspended_games?.length > 0 && <span className="text-[10px] font-bold uppercase tracking-widest text-[#FF9500] flex items-center gap-0.5"><WarningCircle size={12} weight="fill" />Suspended G{e.suspended_games.map((g) => g + 1).join(",")}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isAdmin ? (
                <>
                  {status === "pending" && (
                    <button onClick={() => setStatus(e.id, "approved", "Entry approved")} className="bg-[#34C759] hover:bg-[#34C759]/90 text-black text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-sm" data-testid={`approve-${e.id}`}>Approve</button>
                  )}
                  <Select value={e.payment_status} onValueChange={(v) => setPayment(e.id, v)}>
                    <SelectTrigger className={`w-24 h-9 border-0 font-bold uppercase text-xs tracking-widest ${PAY_STYLE[e.payment_status]}`} data-testid={`payment-select-${e.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 text-muted-foreground hover:text-white border border-border rounded-sm" data-testid={`entry-menu-${e.id}`} aria-label="Player actions"><DotsThreeVertical size={18} weight="bold" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-card border-border" align="end">
                      <DropdownMenuItem onClick={() => setCardFor({ entry: e, type: "yellow" })} data-testid={`yellow-${e.id}`} className="gap-2 cursor-pointer"><span className="inline-block w-2.5 h-3.5 rounded-[1px] bg-[#FFD60A]" /> Give yellow card</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setCardFor({ entry: e, type: "red" })} data-testid={`red-${e.id}`} className="gap-2 cursor-pointer"><span className="inline-block w-2.5 h-3.5 rounded-[1px] bg-kinetic" /> Give red card</DropdownMenuItem>
                      {(e.cards?.length > 0) && <DropdownMenuItem onClick={() => clearCards(e.id)} className="gap-2 cursor-pointer"><X size={16} weight="bold" /> Clear cards & suspensions</DropdownMenuItem>}
                      {status !== "stood_down" ? (
                        <DropdownMenuItem onClick={() => setStatus(e.id, "stood_down", "Player stood down")} data-testid={`stand-down-${e.id}`} className="gap-2 cursor-pointer"><HandPalm size={16} weight="bold" /> Stand down</DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => setStatus(e.id, "approved", "Player reinstated")} data-testid={`reinstate-${e.id}`} className="gap-2 cursor-pointer"><CheckCircle size={16} weight="bold" /> Reinstate</DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => setStatus(e.id, "disqualified", "Player disqualified — no refund")} data-testid={`disqualify-${e.id}`} className="gap-2 cursor-pointer text-kinetic"><Prohibit size={16} weight="bold" /> Disqualify (no refund)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => remove(e.id)} data-testid={`remove-entry-${e.id}`} className="gap-2 cursor-pointer"><Trash size={16} weight="bold" /> Remove entry</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <span className={`text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm ${PAY_STYLE[e.payment_status]}`}>{e.payment_status}</span>
              )}
            </div>
          </div>
        );})}
      </div>
      {cardFor && <CardDialog tournament={tournament} entry={cardFor.entry} type={cardFor.type} onClose={() => setCardFor(null)} onSaved={onChange} />}
    </div>
  );
}

function CardDialog({ tournament, entry, type, onClose, onSaved }) {
  const games = tournament.games || [];
  const firstOpen = games.find((g) => g.status !== "completed") || games[0];
  const [gameIndex, setGameIndex] = useState(String(firstOpen ? firstOpen.index : 0));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const isRed = type === "red";

  const give = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/entries/${entry.id}/card`, { type, game_index: Number(gameIndex), reason });
      if (data.suspension_added) {
        toast.warning(`${entry.display_name} is now suspended for Game ${data.suspended_next_game + 1} (will be scored No Show).`);
      } else {
        toast.success(`${type === "red" ? "Red" : "Yellow"} card recorded for ${entry.display_name}`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-sm">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl flex items-center gap-2">
          <span className={`inline-block w-4 h-5 rounded-[2px] ${isRed ? "bg-kinetic" : "bg-[#FFD60A]"}`} />
          {isRed ? "Red" : "Yellow"} Card
        </DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">For <b className="text-white">{entry.display_name}</b>. {isRed ? "A red card means they miss the next game (scored No Show)." : "2 yellow cards means they miss the next game (scored No Show)."}</p>
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Which game?</label>
          <Select value={gameIndex} onValueChange={setGameIndex}>
            <SelectTrigger className="bg-secondary border-border mt-1.5 h-11" data-testid="card-game-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              {games.map((g) => <SelectItem key={g.index} value={String(g.index)}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1.5">Suspension (if triggered) applies to the <b>next</b> game.</p>
        </div>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="bg-secondary border-border" data-testid="card-reason" />
        <DialogFooter>
          <Button onClick={give} disabled={saving} data-testid="give-card" className={`w-full font-heading font-bold uppercase tracking-widest ${isRed ? "bg-kinetic hover:bg-kinetic/90 text-white" : "bg-[#FFD60A] hover:bg-[#FFD60A]/90 text-black"}`}>{saving ? "Saving…" : `Give ${isRed ? "Red" : "Yellow"} Card`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-border bg-card rounded-sm p-3 text-center">
      <div className="font-heading font-black text-2xl" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

/* ---------------- Admin bar ---------------- */
function AdminBar({ tournament, onChange }) {
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [announcing, setAnnouncing] = useState(false);

  const setStatus = async (status) => {
    await api.patch(`/tournaments/${tournament.id}`, { status });
    toast.success(`Status set to ${status.replace("_", " ")}`);
    onChange();
  };

  const announceWinner = async () => {
    if (!window.confirm("Announce the champion to the group? This posts the final standings to chat and sends a push notification to everyone.")) return;
    setAnnouncing(true);
    try {
      const { data } = await api.post(`/tournaments/${tournament.id}/announce-winner`);
      toast.success(`🏆 ${data.champion} announced! Notified ${data.sent} device(s).`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAnnouncing(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      <Select value={tournament.status} onValueChange={setStatus}>
        <SelectTrigger className="w-40 h-10 bg-card border-border font-bold uppercase text-xs tracking-widest" data-testid="status-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-card border-border">
          <SelectItem value="draft">Draft</SelectItem>
          <SelectItem value="entries_open">Entries Open</SelectItem>
          <SelectItem value="live">Live</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
        </SelectContent>
      </Select>
      {tournament.status === "completed" && (
        <Button onClick={announceWinner} disabled={announcing} data-testid="announce-winner-button" className="h-10 bg-[#FFD60A] hover:bg-[#FFD60A]/90 text-black font-bold uppercase text-xs tracking-widest gap-1.5">
          <Megaphone size={16} weight="fill" /> {announcing ? "Announcing…" : "Announce Winner"}
        </Button>
      )}
      <Button onClick={() => setEditOpen(true)} data-testid="edit-tournament-button" className="h-10 bg-secondary hover:bg-[#1E1E1E] border border-border text-white font-bold uppercase text-xs tracking-widest gap-1.5">
        <PencilSimple size={16} weight="bold" /> Settings
      </Button>
      <Button onClick={() => setBroadcastOpen(true)} data-testid="broadcast-button" className="h-10 bg-secondary hover:bg-[#1E1E1E] border border-border text-white font-bold uppercase text-xs tracking-widest gap-1.5">
        <PaperPlaneTilt size={16} weight="bold" /> Share Update
      </Button>
      {broadcastOpen && <BroadcastDialog tournament={tournament} onClose={() => setBroadcastOpen(false)} />}
      {editOpen && <EditTournamentDialog tournament={tournament} onClose={() => setEditOpen(false)} onSaved={onChange} />}
    </div>
  );
}

function EditTournamentDialog({ tournament, onClose, onSaved }) {
  const [name, setName] = useState(tournament.name);
  const [description, setDescription] = useState(tournament.description || "");
  const [entryFee, setEntryFee] = useState(tournament.entry_fee || 0);
  const [maxEntries, setMaxEntries] = useState(tournament.max_entries || 0);
  const [requiresApproval, setRequiresApproval] = useState(!!tournament.requires_approval);
  const [paymentDeadline, setPaymentDeadline] = useState(toLocalInput(tournament.payment_deadline));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/tournaments/${tournament.id}`, {
        name: name.trim(), description,
        entry_fee: Number(entryFee) || 0,
        max_entries: Number(maxEntries) || 0,
        requires_approval: requiresApproval,
        payment_deadline: paymentDeadline ? new Date(paymentDeadline).toISOString() : null,
      });
      toast.success("Tournament settings updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Tournament Settings</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-secondary border-border mt-1.5" data-testid="et-name" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-secondary border-border mt-1.5" data-testid="et-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Buy-in ($)</label>
              <Input type="number" value={entryFee} onChange={(e) => setEntryFee(e.target.value)} className="bg-secondary border-border mt-1.5" data-testid="et-fee" />
            </div>
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Max Entries (0=∞)</label>
              <Input type="number" value={maxEntries} onChange={(e) => setMaxEntries(e.target.value)} className="bg-secondary border-border mt-1.5" data-testid="et-max" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Payment Deadline</label>
            <input type="datetime-local" value={paymentDeadline} onChange={(e) => setPaymentDeadline(e.target.value)} className="bg-secondary border border-border rounded-sm px-3 h-10 w-full text-base mt-1.5" data-testid="et-deadline" />
          </div>
          <div className="flex items-center justify-between border border-border rounded-sm bg-secondary px-3 py-2.5">
            <div>
              <div className="font-semibold text-sm">Require admin approval</div>
              <div className="text-xs text-muted-foreground">Off = players join instantly</div>
            </div>
            <Switch checked={requiresApproval} onCheckedChange={setRequiresApproval} data-testid="et-approval" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="et-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest">{saving ? "Saving…" : "Save Settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ tournament, onClose }) {
  const [title, setTitle] = useState(`${tournament.name} — Update`);
  const [body, setBody] = useState("The leaderboard has been updated. Check the latest standings!");
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/tournaments/${tournament.id}/broadcast`, { title, body, url: `/tournament/${tournament.id}` });
      toast.success(`Sent to ${data.sent} device(s)`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Share Leaderboard Update</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Push a notification to everyone who enabled reminders.</p>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-secondary border-border" data-testid="broadcast-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="bg-secondary border border-border rounded-sm px-3 py-2 text-base w-full" data-testid="broadcast-body" />
        <DialogFooter>
          <Button onClick={send} disabled={sending} data-testid="send-broadcast" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest">{sending ? "Sending…" : "Send Notification"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
